// src/pages/Profile.js
import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase"; // <-- adjust if your path differs
import { useNavigate } from "react-router-dom"; // if you use react-router

// ---------- helpers ----------
const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";
const notProvided = "Not Provided";

function safeVal(v) {
  return isEmpty(v) ? notProvided : String(v);
}


function emailToDocId(email) {
  return String(email || "")
    .toLowerCase()
    .replace(/[@.]/g, "_"); // jkaye@convalesceuk.com -> jkaye_convalesceuk_com
}



function maskValue(value, { showLast = 3 } = {}) {
  if (isEmpty(value) || value === notProvided) return notProvided;
  const s = String(value);
  if (s.length <= showLast) return "•".repeat(s.length);
  return "•".repeat(Math.max(0, s.length - showLast)) + s.slice(-showLast);
}

function asDateString(v) {
  // handles Firestore Timestamp, ISO string, or DD/MM/YYYY
  if (!v) return "";
  try {
    if (typeof v === "string") return v; // keep as stored
    if (typeof v.toDate === "function") return v.toDate().toLocaleDateString();
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return String(v);
  } catch {
    return String(v);
  }
}



function getInitials({ firstName, lastName, name }) {
  // Prefer full name
  if (name && name !== "Not Provided") {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    if (parts.length) {
      return parts.map(p => p[0].toUpperCase()).join("");
    }
  }

  // Fallback to first + last name
  if (firstName || lastName) {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  }

  // Final fallback
  return "👤";
}


// ---------- UI atoms ----------
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "12px 10px",
        borderRadius: 12,
        border: active ? "1px solid rgba(17,24,39,0.25)" : "1px solid rgba(0,0,0,0.08)",
        background: active ? "#ffffff" : "rgba(255,255,255,0.55)",
        fontWeight: 800,
        cursor: "pointer",
        color: "#111827",
      }}
    >
      {children}
    </button>
  );
}

function Section({ title, actionLabel, onAction, children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
        padding: 14,
        marginTop: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>{title}</div>
        <div style={{ flex: 1 }} />
        {!!actionLabel && (
          <button
            onClick={onAction}
            style={{
              border: "1px solid rgba(0,0,0,0.12)",
              background: "#fff",
              padding: "8px 10px",
              borderRadius: 12,
              cursor: "pointer",
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            {actionLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, sub }) {
  const display = safeVal(value);
  const isNP = display === notProvided;

  return (
    <div style={{ padding: "10px 4px", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: isNP ? "#9ca3af" : "#111827" }}>
        {display}
      </div>
      {!!sub && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function RolePill({ role }) {
  const { text, bg, fg } = useMemo(() => {
    const r = (role || "user").toLowerCase();
    if (r === "admin") return { text: "Admin", bg: "#fee2e2", fg: "#991b1b" };
    if (r === "staff") return { text: "Staff", bg: "#dbeafe", fg: "#1e40af" };
    return { text: "User", bg: "#dcfce7", fg: "#166534" };
  }, [role]);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        fontWeight: 900,
        fontSize: 12,
        background: bg,
        color: fg,
      }}
    >
      {text}
    </span>
  );
}

// ---------- main ----------
export default function Profile() {
  const navigate = useNavigate?.();
  const [tab, setTab] = useState("profile"); // profile | job | feedback
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showSensitive, setShowSensitive] = useState(false);

  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setErr("");
      setUser(u);

      if (!u) {
        setProfile(null);
        setLoading(false);
        if (unsubUserDoc) unsubUserDoc();
        return;
      }

      setLoading(true);

      // Your Firestore profile doc:
     const userDocId = emailToDocId(u.email);
	const ref = doc(db, "users", userDocId);


      unsubUserDoc = onSnapshot(
        ref,
        (snap) => {
          const d = snap.exists() ? snap.data() : {};

          // Flexible mapping (supports both nested and top-level)
          const firstName = d.firstName || d.firstname || "";
          const lastName = d.lastName || d.lastname || "";
          const fullName =
            d.name ||
            d.fullName ||
            [firstName, lastName].filter(Boolean).join(" ") ||
            u.displayName ||
            "—";

          const email = d.email || d.primaryEmail || d.contact?.email || u.email || "—";
          const phone = d.phone || d.contact?.phone || "";
          const address = d.address || d.contact?.address || "";

          const jobTitle = d.jobTitle || d.position || d.roleTitle || ""; // e.g. "Support Worker"
          const role = d.role || "user"; // user | staff | admin

          const emergency = d.emergencyContact || d.emergency || {};
          const compliance = d.compliance || {};
          const payroll = d.payroll || {};

          setProfile({
            uid: u.uid,
            photoURL: d.photoURL || u.photoURL || "",
            name: fullName,
            preferredName: d.preferredName || "",
            title: d.title || "",
            firstName,
            lastName,
            middleName: d.middleName || "",
            dob: d.dateOfBirth || d.dob || "",

            email,
            phone,
            address,

            role,
            jobTitle,

            emergency: {
              name: emergency.name || emergency.fullName || "",
              relationship: emergency.relationship || "",
              email: emergency.email || "",
              phone: emergency.phone || "",
            },

            compliance: {
              wtdOptOutDate: compliance.wtdOptOutDate || d.wtdOptOutDate || "",
              dataProtectionConsent: compliance.dataProtectionConsent || d.dataProtectionConsent || "",
              rtwLimitations: compliance.rtwLimitations || d.rtwLimitations || "",
              workRights: compliance.workRights || d.workRights || "",
              workRightsExpiry: compliance.workRightsExpiry || d.workRightsExpiry || "",
            },

            payroll: {
              niNumber: payroll.niNumber || d.niNumber || "",
              payrollNumber: payroll.payrollNumber || d.payrollNumber || "",
              continuousServiceDate: payroll.continuousServiceDate || d.continuousServiceDate || "",
            },
          });

          setLoading(false);
        },
        (e) => {
          console.error(e);
          setErr("Could not load your profile. Please try again.");
          setLoading(false);
        }
      );
    });

    return () => {
      if (unsubUserDoc) unsubUserDoc();
      unsubAuth();
    };
  }, []);

  const headerBg = useMemo(() => {
    // Replace with your own image if you want:
    // return `url("/images/profile-header.jpg")`;
    return "linear-gradient(135deg, rgba(37,99,235,0.22), rgba(236,72,153,0.18))";
  }, []);

  if (loading) {
    return (
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "14px 14px 80px" }}>
        <div style={{ height: 160, borderRadius: 18, background: "rgba(0,0,0,0.06)" }} />
        <div style={{ height: 120, marginTop: -50, borderRadius: 18, background: "#fff" }} />
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "14px 14px 80px" }}>
        <div
          style={{
            background: "#fff1f2",
            border: "1px solid #fecaca",
            borderRadius: 16,
            padding: 14,
          }}
        >
          <div style={{ fontWeight: 900, color: "#9f1239" }}>Error</div>
          <div style={{ color: "#881337", marginTop: 6 }}>{err}</div>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "14px 14px 80px" }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid rgba(0,0,0,0.08)",
            padding: 14,
          }}
        >
          <div style={{ fontWeight: 900 }}>You’re signed out</div>
          <div style={{ marginTop: 6, color: "#6b7280" }}>Please log in to view your profile.</div>
        </div>
      </div>
    );
  }

  const displayDob = showSensitive ? safeVal(asDateString(profile.dob)) : maskValue(asDateString(profile.dob), { showLast: 4 });
  const displayNI = showSensitive ? safeVal(profile.payroll.niNumber) : maskValue(profile.payroll.niNumber, { showLast: 2 });

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", paddingBottom: 90 }}>
      {/* Hero */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            height: 190,
            borderRadius: 0,
            background: headerBg,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        />

        <div style={{ padding: "0 14px" }}>
          <div
            style={{
              marginTop: -62,
              background: "#fff",
              borderRadius: 18,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 18px 34px rgba(0,0,0,0.08)",
              padding: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Avatar */}
              <div
                style={{
                  width: 78,
                  height: 78,
                  borderRadius: "50%",
                  border: "6px solid #fff",
                  marginTop: -42,
                  background: "#f3f4f6",
                  overflow: "hidden",
                  boxShadow: "0 12px 18px rgba(0,0,0,0.10)",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 1000,
                  fontSize: 26,
                  color: "#1f2937",
                }}
              >
                {profile.photoURL ? (
                  <img
                    src={profile.photoURL}
                    alt="Profile"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  getInitials({
					firstName: profile.firstName,
					lastName: profile.lastName,
					name: profile.name,
				  })
                )}
              </div>

              {/* Name + role */}
              <div style={{ flex: 1, paddingTop: 6 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontSize: 22, fontWeight: 1000, color: "#111827" }}>{profile.name}</div>
                  <RolePill role={profile.role} />
                </div>
                <div style={{ marginTop: 6, color: "#6b7280", fontWeight: 800 }}>
                  {safeVal(profile.jobTitle) === notProvided ? "—" : profile.jobTitle}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <TabButton active={tab === "profile"} onClick={() => setTab("profile")}>
                Profile
              </TabButton>
              <TabButton active={tab === "job"} onClick={() => setTab("job")}>
                Job
              </TabButton>
              <TabButton active={tab === "feedback"} onClick={() => setTab("feedback")}>
                Feedback
              </TabButton>
            </div>
          </div>

          {/* Content */}
          {tab === "profile" && (
            <>
              <Section
                title="Personal Information"
                actionLabel="Update Personal Information"
                onAction={() => (navigate ? navigate("/profile/edit") : alert("Hook this button to your edit screen"))}
              >
                <Row label="Title" value={profile.title} />
                <Row label="Full Name" value={profile.name} />

                <Row label="Middle Name" value={profile.middleName} />
                <Row label="Preferred Name" value={profile.preferredName} />
                <Row label="Date of Birth" value={displayDob} />
                <div style={{ padding: "10px 4px", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                  <button
                    onClick={() => setShowSensitive((s) => !s)}
                    style={{
                      width: "100%",
                      border: "1px solid rgba(0,0,0,0.12)",
                      background: "#fff",
                      padding: "10px 12px",
                      borderRadius: 12,
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    {showSensitive ? "Hide sensitive details" : "Show sensitive details"}
                  </button>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                    Tip: keep NI/DOB masked by default for privacy.
                  </div>
                </div>

                <Row
                  label="Emergency Contact Details"
                  value={
                    [profile.emergency.name, profile.emergency.relationship].filter(Boolean).join(" ") || ""
                  }
                  sub={[profile.emergency.email, profile.emergency.phone].filter(Boolean).join(" • ")}
                />
              </Section>

              <Section title="Contact">
                <Row label="Email Address" value={profile.email} />
                <Row label="Phone Number" value={profile.phone} />
                <Row label="Address" value={profile.address} />
              </Section>

              <Section title="Compliance">
                <Row label="WTD opt out date" value={asDateString(profile.compliance.wtdOptOutDate)} />
                <Row label="Data protection consent" value={profile.compliance.dataProtectionConsent} />
                <Row label="RtW limitations" value={profile.compliance.rtwLimitations} />
                <Row
                  label="Work Rights"
                  value={profile.compliance.workRights}
                  sub={
                    isEmpty(profile.compliance.workRightsExpiry)
                      ? ""
                      : `Expiry: ${asDateString(profile.compliance.workRightsExpiry)}`
                  }
                />
              </Section>

              <Section title="Payroll Information">
                <Row label="National Insurance Number" value={displayNI} />
                <Row label="Continuous Service Date" value={asDateString(profile.payroll.continuousServiceDate)} />
                <Row label="Payroll Number" value={profile.payroll.payrollNumber} />
              </Section>
            </>
          )}

          {tab === "job" && (
            <>
              <Section title="Job">
                <Row label="Job Title" value={profile.jobTitle} />
                <Row label="Role" value={profile.role} />
              </Section>

              <Section title="Work & Compliance Summary">
                <Row label="Right to Work" value={profile.compliance.workRights} />
                <Row label="Right to Work Expiry" value={asDateString(profile.compliance.workRightsExpiry)} />
                <Row label="WTD Opt-out" value={asDateString(profile.compliance.wtdOptOutDate)} />
              </Section>

              <Section
                title="Quick Actions"
                actionLabel="Open My Shifts"
                onAction={() => (navigate ? navigate("/myshifts") : alert("Link this to your shifts route"))}
              >
                <Row label="Tip" value="You can add Next Shift + Compliance Status badges here." />
              </Section>
            </>
          )}

          {tab === "feedback" && (
            <>
              <Section
                title="Feedback"
                actionLabel="Send Feedback"
                onAction={() => (navigate ? navigate("/feedback") : alert("Hook this to your feedback screen"))}
              >
                <Row label="Help us improve" value="Report a bug, request a feature, or share suggestions." />
              </Section>
            </>
          )}

          
        </div>
      </div>
    </div>
  );
}
